import { createColumnHelper } from '@tanstack/react-table';
import { format } from 'date-fns';
import { useTranslation } from 'next-i18next';
import Tooltip from 'react-tooltip-lite';
import * as yup from 'yup';

import { PrintFieldQr } from '@/modules/settings/property';
import { useGetActiveCountries } from '@/modules/settings/property/apiHooks';
import ArchiveButton from '@/modules/settings/property/components/Fields/shared/ArchiveField/ArchiveButton';
import WarehouseActions from '@/modules/settings/property/components/Warehouses/Actions';
import { IPropertySettings } from '@/modules/settings/property/types';
import { IWarehouseManagement } from '@/modules/warehouseManagement/shared/types';
import { CircularBgImage, Typography } from '@/shared/components/atoms';
import { AssetListProps } from '@/shared/components/molecules/ChooseAsset/ChooseAsset';
import PartialListWithTooltip from '@/shared/components/molecules/PartialListWithTooltip';
import { EntityType } from '@/shared/constant/enums';
import { useModal } from '@/shared/hooks/context/modalContext/useModal';
import { getLocalizedDate } from '@/shared/lib/dates';
import { IGeneral } from '@/shared/types';

import ExleminationMark from '~/images/shared/ExleminationMark.svg';
import QRCode from '~/images/shared/QRCodeRounded.svg';

export const usePropertySettings = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const { openModal } = useModal();

  //******************** edit property details ********************/

  const EditPropertyDetailsSchema: yup.SchemaOf<IPropertySettings.IProperty> =
    yup.object({
      name: yup.string().required(t('this-field-is-required')),
      type: yup.string().required('this-field-is-required'),
      areaUnit: yup.string().defined().nullable(),
      totalArea: yup
        .number()
        .transform((value) => (isNaN(value) ? null : value))
        .min(1, t('non-zero-field'))
        .nullable()
        .defined(),
      location: yup.string().nullable().defined(),
      country: yup.string().required(t('this-field-is-required')),
    });

  const getEditPropertyMutation = (
    data: IPropertySettings.IProperty
  ): IPropertySettings.IUpdatePropertyDTO => {
    const { type: _type, areaUnit: _area, ...rest } = data;
    return {
      ...rest,
      totalArea: data.totalArea ? data.totalArea : 0,
      location: data.location ? data.location : '-',
    };
  };

  //******************** property fields ********************/

  const getPropertyFieldsTable = () => {
    const columnHelper = createColumnHelper<IPropertySettings.IPropertyField>();
    return [
      columnHelper.accessor('name', {
        header: t('name'),
        cell: ({ getValue }) => getValue(),
      }),
      columnHelper.accessor('cropName', {
        header: t('crop'),
        cell: ({ getValue, row: { original } }) => (
          <div className='flex items-center gap-8'>
            <CircularBgImage
              imageSrc={original.cropImage}
              imageClassName='scale-125'
              bgColor='bg-surface-30'
            />
            <div>
              <Typography weight='semiBold'>{getValue()}</Typography>
              <Typography variant='subtitle2' className='text-surface-10'>
                {original?.plantationDate
                  ? format(new Date(original.plantationDate), 'MMM d, yyyy')
                  : '-'}
              </Typography>
            </div>
          </div>
        ),
      }),
      columnHelper.accessor('varietyName', {
        header: t('Crop Variety'),
        cell: ({ getValue }) => (
          <Typography className='text-surface-10'>
            {getValue() ?? '-'}
          </Typography>
        ),
      }),
      columnHelper.accessor('type', {
        header: t('Field Type'),
        cell: ({ getValue }) => (
          <Typography className='text-surface-10'>
            {t(getValue().toUpperCase())}
          </Typography>
        ),
      }),
      columnHelper.accessor('area', {
        header: t('area'),
        cell: ({ getValue, row: { original } }) => (
          <Typography className='text-surface-10'>{`${getValue()} ${t(
            original.areaUnit
          )}`}</Typography>
        ),
      }),
      columnHelper.accessor('updatedAt', {
        header: t('archived-date'),
        cell: ({ getValue }) => {
          const val = getValue();
          return (
            <Typography className='text-surface-10'>
              {val
                ? getLocalizedDate({
                    date: val,
                    formatDate: 'dd/MM/yyyy, h:mm a',
                    locale: language,
                  })
                : '-'}
            </Typography>
          );
        },
      }),
      columnHelper.accessor('archivedByName', {
        header: t('archived-by'),
        cell: ({ getValue }) => getValue() ?? '-',
      }),
      columnHelper.display({
        id: 'fieldActions',
        cell: ({ row: { original } }) => (
          <div
            className='flex items-center justify-end gap-24'
            onClick={(e) => e.stopPropagation()}
          >
            <span
              className='cursor-pointer text-primary-default'
              onClick={() => {
                openModal(<PrintFieldQr field={original} />);
              }}
            >
              <QRCode width='24' height='24' />
            </span>
            <ArchiveButton fieldId={original.id} />
          </div>
        ),
      }),
    ];
  };

  const createEditPackhouseFieldSchema: yup.SchemaOf<IPropertySettings.ICreateEditPackhouseFieldFormValues> =
    yup.object({
      name: yup.string().required(t('this-field-is-required')),
      type: yup.string().required(t('this-field-is-required')),
    });

  const createEditPropertyFieldSchema: yup.SchemaOf<IPropertySettings.ICreateEditFieldFormValues> =
    yup.object({
      name: yup.string().required(t('this-field-is-required')),
      type: yup.string().required(t('this-field-is-required')),
      area: yup
        .number()
        .transform((value) => (isNaN(value) ? undefined : value))
        .required(t('this-field-is-required')),
      areaUnit: yup.string().required(t('this-field-is-required')),
      plantationDate: yup.string(),
      cropId: yup.number().required(t('this-field-is-required')),
      varietyId: yup
        .number()
        .transform((value) => (isNaN(value) ? undefined : value))
        .notRequired(),
    });

  const getEntityTypeId = (
    propertyId: number | undefined,
    assetsList: AssetListProps | undefined
  ) => {
    if (assetsList) {
      const asset = [...assetsList.farms, ...assetsList.packhouses].find(
        (asset) => asset.id === propertyId
      );
      if (asset) {
        return asset.entityTypeId;
      }
    }

    return EntityType.PACKHOUSE;
  };

  //******************** property jobs ********************/

  const getPropertyJobsTable = (
    _onEditClick: (operation: IPropertySettings.IPropertyJob) => void
  ) => {
    const columnHelper = createColumnHelper<IPropertySettings.IPropertyJob>();
    return [
      columnHelper.accessor('name', {
        header: t('Task Name'),
        cell: ({
          getValue,
          row: {
            original: { isCustom },
          },
        }) => (
          <div className='flex items-center gap-2'>
            <span>{getValue()}</span>
            {isCustom ? (
              <Tooltip
                content={
                  <div className='relative flex w-fit min-w-[180px] max-w-xs gap-16 rounded-lg bg-background-neutral-99 py-8 px-16 text-center shadow-100'>
                    <Typography variant='caption'>
                      {t('custom-operation-description')}
                    </Typography>
                  </div>
                }
                direction='down'
                arrow={false}
                tipContentHover
              >
                <span className='text-primary-default'>
                  <ExleminationMark />
                </span>
              </Tooltip>
            ) : (
              ''
            )}
          </div>
        ),
      }),
      columnHelper.accessor('notes', {
        header: t('operation-description'),
        cell: ({ getValue }) => getValue() ?? '-',
      }),
      // columnHelper.accessor('isCustom', {
      //   header: '',
      //   cell: ({ getValue, row: { original } }) =>
      //     getValue() ? (
      //       <div className='flex items-center justify-center text-24'>
      //         <span
      //           className=' cursor-pointer'
      //           onClick={() => onEditClick(original)}
      //         >
      //           <Edit />
      //         </span>
      //       </div>
      //     ) : (
      //       ''
      //     ),
      // }),
    ];
  };

  //******************** property configuration ********************/

  const propertyConfigurationSchema: yup.SchemaOf<IPropertySettings.IPropertyConfigurationFormValues> =
    yup.object({
      timeZone: yup.string().nullable().required(t('this-field-is-required')),
      currency: yup.string().nullable().required(t('this-field-is-required')),
      measurementUnits: yup
        .array()
        .of(yup.number().required())
        .required(t('this-field-is-required'))
        .typeError(t('this-field-is-required'))
        .min(1, t('this-field-is-required')),
      areaUnit: yup.string().nullable().required(t('this-field-is-required')),
      measurementSystem: yup.string().required('this-field-is-required'),
      isInventoryActive: yup.boolean().required(t('this-field-is-required')),
      inventoryApproval: yup.boolean().required(t('this-field-is-required')),
      isOnlyApprovedSettlementsActive: yup
        .boolean()
        .required(t('this-field-is-required')),
      settlementLevel: yup
        .mixed<IGeneral.ISettlementLevel>()
        .required(t('this-field-is-required')),
    });

  //******************** property warehouses ********************/

  const getWarehousesTable = () => {
    const columnHelper =
      createColumnHelper<IWarehouseManagement.WarehouseObjectSchema>();
    return [
      columnHelper.accessor('name', {
        header: t('name'),
        cell: ({ getValue }) => getValue(),
      }),
      columnHelper.accessor('categories', {
        header: t('Stock Type'),
        cell: ({ getValue }) => (
          <PartialListWithTooltip
            limit={2}
            items={getValue().map((item) => ({
              description: item.name,
            }))}
          />
        ),
      }),
      columnHelper.accessor('responsible', {
        header: t('Access Users'),
        cell: ({ getValue }) => (
          <PartialListWithTooltip
            limit={3}
            items={getValue().map((item) => ({
              description: item.name,
            }))}
          />
        ),
      }),
      columnHelper.display({
        id: 'propertyWarehouseActions',
        cell: ({ row: { original } }) => (
          <div className='flex justify-end'>
            <WarehouseActions warehouse={original} />
          </div>
        ),
      }),
    ];
  };

  const getArchivedWarehousesTable = () => {
    const columnHelper =
      createColumnHelper<IPropertySettings.IArchivedWarehouse>();
    const warehouseTable = getWarehousesTable();
    return [
      ...warehouseTable.slice(0, warehouseTable.length - 1),
      columnHelper.accessor('deactivatedByName', {
        header: t('deactivated-by'),
        cell: ({ getValue }) => getValue(),
      }),
      columnHelper.accessor('deactivatedAt', {
        header: t('deactivation-time'),
        cell: ({ getValue }) =>
          getLocalizedDate({
            date: getValue(),
            formatDate: 'dd/MM/yyyy, h:mm a',
            locale: language,
          }),
      }),
    ];
  };

  const useGetCountryID = (country: string | undefined) => {
    const { data: activeCountries } = useGetActiveCountries(!!country);
    return activeCountries && country
      ? activeCountries.find((activeCountry) => activeCountry.name === country)
          ?.id
      : undefined;
  };

  const useAddEditWarehouse = () => {
    const ALPHANUMERIC_REGEX = /^[a-zA-Z0-9\p{L}\p{N}\s]+$/u;
    const validationSchema = yup.object({
      name: yup
        .string()
        .required(t('this-field-is-required'))
        .matches(ALPHANUMERIC_REGEX, t('this-field-is-char-and-nums-only'))
        .max(
          100,
          t('this-field-not-accept-more-than-x-character', { count: 100 })
        ),
      categories: yup
        .array()
        .of(yup.number())
        .required(t('this-field-is-required'))
        .min(1, t('this-field-is-required')),
      responsible: yup
        .array()
        .of(yup.number())
        .required(t('this-field-is-required'))
        .min(1, t('this-field-is-required')),
    });

    const getCategoriesList = (
      categories: IPropertySettings.IGetWarehouseCategoriesResponse | undefined
    ) => {
      return categories?.items.map((category) => {
        return {
          label: category.name,
          value: category.id,
        };
      });
    };

    const getWarehouseSpecialistsList = (
      specialists: IPropertySettings.GetWarehouseSpecialistsResponse | undefined
    ) => {
      return specialists?.map((specialist) => {
        return {
          label: `${specialist.firstName} ${specialist.lastName}`,
          value: specialist.id,
        };
      });
    };

    return {
      validationSchema,
      getCategoriesList,
      getWarehouseSpecialistsList,
    };
  };

  return {
    EditPropertyDetailsSchema,
    createEditPropertyFieldSchema,
    createEditPackhouseFieldSchema,
    getEditPropertyMutation,
    getPropertyFieldsTable,
    getPropertyJobsTable,
    propertyConfigurationSchema,
    useGetCountryID,
    getWarehousesTable,
    getArchivedWarehousesTable,
    useAddEditWarehouse,
    getEntityTypeId,
  };
};
